import { useEffect, useState } from "react";
import {
  subscribeOfflineOutbox,
  subscribeOfflineSyncState,
  syncOfflineOutbox,
  initOfflineOutbox,
  getOfflineSyncState,
  type PendingWrite,
  type OfflineSyncState,
} from "@/lib/offline-outbox";
import { subscribeOutbox, drainOutbox } from "@/lib/prescription-outbox";
import {
  subscribeUploadOutbox,
  syncUploadOutbox,
  initUploadOutbox,
  type PendingUpload,
} from "@/lib/upload-outbox";
import {
  subscribeQueueOutbox,
  subscribeQueueDrafts,
  syncQueueOutbox,
  type PendingQueueAction,
  type PendingAppointmentDraft,
} from "@/lib/queue-outbox";

/**
 * Single source of truth for the whole app's offline state: how many writes are
 * waiting (generic writes + prescriptions + queue actions/drafts), whether a
 * replay is running, and a manual "sync now" trigger.
 */
export function useOfflineSync() {
  const [online, setOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine,
  );
  const [writes, setWrites] = useState<PendingWrite[]>([]);
  const [rxCount, setRxCount] = useState(0);
  const [queueActions, setQueueActions] = useState<PendingQueueAction[]>([]);
  const [queueDrafts, setQueueDrafts] = useState<PendingAppointmentDraft[]>([]);
  const [uploads, setUploads] = useState<PendingUpload[]>([]);
  const [state, setState] = useState<OfflineSyncState>(() => getOfflineSyncState());

  useEffect(() => {
    initOfflineOutbox();
    initUploadOutbox();
    const unsubs = [
      subscribeOfflineOutbox(setWrites),
      subscribeOfflineSyncState(setState),
      subscribeOutbox(setRxCount),
      subscribeQueueOutbox(setQueueActions),
      subscribeQueueDrafts(setQueueDrafts),
      subscribeUploadOutbox(setUploads),
    ];
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      unsubs.forEach((u) => u());
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  const pendingCount =
    writes.length + rxCount + queueActions.length + queueDrafts.length + uploads.length;
  const failedCount =
    writes.filter((w) => w.attempts > 0).length +
    queueActions.filter((a) => a.attempts > 0).length +
    queueDrafts.filter((d) => d.attempts > 0).length +
    uploads.filter((u) => u.attempts > 0).length;

  const syncNow = async () => {
    await Promise.all([
      syncOfflineOutbox(),
      syncQueueOutbox(),
      drainOutbox(),
      syncUploadOutbox(),
    ]);
  };

  return {
    online,
    writes,
    uploads,
    pendingUploadCount: uploads.length,
    pendingCount,
    failedCount,
    syncing: state.syncing,
    syncProgress:
      state.total > 0 ? Math.min(100, Math.round((state.done / state.total) * 100)) : 0,
    lastSyncedAt: state.lastSyncedAt,
    lastError: state.lastError,
    syncNow,
  };
}
